<template>
  <div class="flex gap-3 w-full" :class="message.role === 'user' ? 'justify-end' : ''">
    <!-- Avatar for assistant -->
    <div v-if="message.role === 'assistant'" class="flex-shrink-0">
      <div class="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
        <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    </div>

    <!-- Message content -->
    <div class="flex-shrink min-w-0" :style="{ maxWidth: '85%' }">
      <div
        class="rounded-2xl px-4 py-3 break-words overflow-wrap-anywhere"
        :class="message.role === 'user'
          ? 'bg-violet-600 text-white'
          : 'bg-gray-100 text-gray-900'"
      >
        <!-- User messages: plain text -->
        <div v-if="message.role === 'user'" class="text-[15px] leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">
          {{ message.content }}
        </div>
        <!-- Assistant messages: limit message with markdown -->
        <div v-else-if="isLimitMessage" class="text-[15px] leading-relaxed markdown-content" v-html="renderedContent"></div>
        <!-- Assistant messages: prompt text -->
        <div v-else>
          <div class="text-[15px] leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere font-mono bg-white rounded-lg p-3 border border-gray-200">
            {{ message.content }}
          </div>
          <!-- Copy button -->
          <button
            @click="$emit('copy', message.content)"
            class="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Копировать промпт
          </button>
        </div>
      </div>
      <!-- Timestamp -->
      <div
        class="text-xs text-gray-500 mt-1 px-1"
        :class="message.role === 'user' ? 'text-right' : ''"
      >
        {{ formatTime(message.timestamp) }}
      </div>
    </div>

    <!-- Avatar for user -->
    <div v-if="message.role === 'user'" class="flex-shrink-0">
      <div class="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center">
        <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import { renderMarkdown } from '../../lib/markdownRenderer.js'

defineEmits(['copy'])

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

// Check if message looks like a limit exceeded error (contains markdown link)
const isLimitMessage = computed(() => {
  return props.message.content && props.message.content.includes('[Активировать триал')
})

const renderedContent = computed(() => {
  if (props.message.role === 'assistant' && isLimitMessage.value) {
    return renderMarkdown(props.message.content)
  }
  return props.message.content
})

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.overflow-wrap-anywhere {
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}

/* Markdown content styling for limit messages */
.markdown-content :deep(p) {
  margin-bottom: 0.5em;
}

.markdown-content :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.markdown-content :deep(li) {
  margin: 4px 0;
}

.markdown-content :deep(a) {
  color: #7c3aed;
  text-decoration: underline;
}
</style>
